import React, { useContext, useRef, useState } from 'react'
import styles from './styles.module.css'
import SyntaxHighlighter from '../../../components/SyntaxHighlighter'
import { FaPlay, FaPause } from 'react-icons/fa'
import {
  barLineSimple,
  barMargin,
  beautifulCombinedBarLine,
  chartWithReferenceLine1,
  data,
  multiBarMargin,
  negativeBar,
  neonBars3D,
  neonBars3DWithTooltip,
  secondaryYAxisType
} from './data'
import ProTip from '../../../components/ProTip'
import { AppContext } from '../../../AppContext'
import { appConstants, domain, lineStyle } from '../../../constants'

const yAxisSidePropText = `yAxisSide={yAxisSides.RIGHT}`

export const headers = {
  minimal: { id: 'minimal', label: 'Minimal chart' },
  styling: { id: 'styling', label: 'Styling' },
  label: { id: 'label', label: 'Label' },
  propsIllustration: { id: 'propsIllustration', label: 'Props Illustration' },
  dataArray: { id: 'dataArray', label: 'Data Array' },
  showGradient: { id: 'showGradient', label: 'Gradient' },
  prefixSuffix: { id: 'prefixSuffix', label: 'Prefix Suffix' },
  yAxis: { id: 'yAxis', label: 'Y Axis Labels' },
  xAxisLabels: { id: 'xAxisLabels', label: 'X Axis Labels' },
  xAxisShift: {
    id: 'xAxisShift',
    label: 'xAxisLabelsVerticalShift and labelsDistanceFromXaxis'
  },
  touchScroll: { id: 'touchScroll', label: 'Touchable, onScroll' },
  scrollRef: { id: 'scrollRef', label: 'scrollRef' },
  threeD: { id: 'threeD', label: 'Three D' },
  animated: { id: 'animated', label: 'Animated' },
  secondaryYAxis: { id: 'secondaryYAxis', label: 'Secondary Y Axis' },
  cap: { id: 'cap', label: 'Capped Bars' },
  customised: { id: 'customised', label: 'Customised chart' },
  pattern: { id: 'pattern', label: 'Background Pattern' },
  negative: { id: 'negative', label: 'Negative values' },
  yAxisSide: { id: 'yAxisSide', label: 'Y Axis Side' },
  referenceLine: { id: 'referenceLine', label: 'Reference Lines' },
  barMarginBottom: { id: 'barMarginBottom', label: 'barMarginBottom' },
  barStyle: { id: 'barStyle', label: 'barStyle' },
  tooltip: { id: 'tooltip', label: 'Tooltip' },
  combinedBarLine: { id: 'combinedBarLine', label: 'Combined Bar & Line' }
}

export const MainBody = (props) => {
  const { isMobile, onBodyScroll } = props
  const appContext = useContext(AppContext)
  const { theme, width } = appContext
  const { sideDrawerWidth, rightNavWidth } = appConstants

  const isSideDrawerVisible = width > 1100

  const mainBodyWidth = isMobile
    ? width
    : width -
      rightNavWidth -
      (isSideDrawerVisible ? sideDrawerWidth - 10 : 30)

  const [isPlaying, setIsplaying] = useState(false)
  const videoRef = useRef()

  const onplayClick = () => {
    if (isPlaying) {
      videoRef.current.pause()
      setIsplaying(false)
    } else {
      videoRef.current.play()
      setIsplaying(true)
    }
  }
  const onVideoEnd = () => {
    setIsplaying(false)
  }

  return (
    <div
      className={styles.container}
      style={{ paddingLeft: isMobile ? 3 : 20, width: mainBodyWidth }}
      onScroll={onBodyScroll}
    >
      <h2>
        Bar Charts
        <a
          style={{ marginLeft: 20, fontSize: 15, color: theme==='dark'? 'skyblue' : 'blue' }}
          target='_blank'
          href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/BarChart/BarChartProps.md'
          rel='noreferrer'
        >
          props table
        </a>
      </h2>
      <div style={{ marginLeft: 10, marginBottom: 100 }}>
        <h4 id={headers.minimal.id}>Minimal Bar Chart</h4>
        <div style={{ marginLeft: 10 }}>
          The simplest Bar chart can be made with just-
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[0].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[0].imgSrc} className={styles.barOutput} alt='' />
        </div>

        <hr />

        <h4 id={headers.styling.id}>Styling the Chart</h4>
        <div style={{ marginLeft: 10 }}>
          {`In the above code snippet, at line number 5, add the prop `}
          <span
            style={{
              fontStyle: 'italic',
              fontSize: 16,
              fontWeight: 'bold',
              color: '#177AD5'
            }}
          >{`frontColor={'#177AD5'}`}</span>
          <p>This will result in-</p>
          <img
            src={require('../../../assets/blue.png')}
            className={styles.barOutput}
            alt=''
          />

          <p>
            Adding the frontColor prop changes the color of the bars, similary
            adding the <b>barWidth</b> changes the width of the bars
          </p>
          <p>
            The new code after adding the frontColor and the barWidth props-
          </p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[1].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[1].imgSrc} className={styles.barOutput} alt='' />

          <p>
            Similary there are various props available to customize the bars,
            like-
          </p>
          <ul>
            <li>roundedTop</li>
            <li>roundedBottom</li>
            <li>barBorderRadius and others</li>
          </ul>

          <hr />
          <p>
            So now we know styling the bars. But did you observe that the styles
            we supply through props are applied to all the bars?
            <br />
            What if we want some styles to be applied to only specific bars?{' '}
          </p>
          <p>
            That's so simple. Just put the style props in the data array,
            instead of directly putting it with the BarChart component.
          </p>
          <p>So, let's try to draw the following BarChart-</p>
          <img src={data[2].imgSrc} className={styles.barOutput} alt='' />
          <ul>
            <li>
              The first challenge we have here is to color the specific bars.
              This can achieved by putting the frontColor prop in the data
              array.
            </li>
            <li>
              Also, we add the corresponding labels-{' '}
              <span style={{ fontStyle: 'italic' }}>
                M, T, W, T, F, S and S{' '}
              </span>{' '}
              in the data array.
            </li>
            <li>
              Next, we set the values of the bars, i.e{' '}
              <span style={{ fontStyle: 'italic' }}>
                250, 500, 745, 320, 600, 256, 300.
              </span>
            </li>
            <li>
              Next, we set the prop <b>noOfSections</b>=3. noOfSections is the
              number of parts in which the y axis is divided.
            </li>
            <li>
              Did you notice that the x axis and the y axis lines are not
              visible in the above chart? For this we set <b>yAxisThickness</b>
              =0 and <b>xAxisThickness</b>=0.
            </li>
          </ul>
          <p>So, the final code for the above chart will be-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[2].code}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h4 id={headers.label.id}>
          The <i>label</i>, <i>labelComponent</i>, and <i>topLabelComponent</i>{' '}
          properties
        </h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            In the above chart we have used the <b>label</b> property with each
            data object, due to which we can see the labels- M, T, W... below
            each bar We can pass text values to the <b>label</b> property and
            customise its style using the <b>labelTextStyle</b> prop.
            <br />
            But sometimes, we may want the label to be a custom component like
            an <i>icon</i>, <i>image</i> or a some UI component of our own
            choice. In such a case we can use the <b>labelComponent</b>{' '}
            property.
          </p>
          <p>
            But what if we need to show the values at the top of the bars.
            Something like this-
          </p>
          <img src={data[3].imgSrc} className={styles.barOutput} alt='' />
          <p>
            Notice the text <b>50</b> above the tallest bar. This is done using
            the <b>topLabelComponent</b> property.
            <br />
            Here's the code for this chart-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[3].code}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h4 id={headers.propsIllustration.id}>
          Understanding various controls/props
        </h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Gifted Chart allows you to make almost any kind of chart you can
            imagine- or at least most of them.
            <br />
            <br />
            With such great customizability, there are chances, you may miss
            some features, if we don't showcase them in the documents properly.
            <br />
            The chart below demonstartes what some major <b>props</b> represent-
          </p>
          <img src={require('../../../assets/barLabelled.png')} alt='' />
          <br />
          <span className={styles.noteText}>Note: </span> We have received
          complains that -{' '}
          <i>
            Setting height, maxValue, stepValue, stepHeight, or noOfSections
            breaks the chart
          </i>
          <p>
            Here are some precautions-
            <br />
            <br />
            <ol>
              <li>
                Please make sure to follow the relation-
                <span style={{
                  paddingLeft: 8,
                  color: 'skyblue'
                }}>
                  maxValue
                </span>
                {' = '}
                <span
                style={{
                  color: 'orange'
                }}
              >
                  noOfSections
                </span>
                {' * '}
                <span
                style={{
                  color: 'orange'
                }}
              >
                  stepValue
                </span>
              </li>
              <li>
                Whenever you change the props like{' '}
                <b>height, maxValue, stepValue, stepHeight,</b> or{' '}
                <b>noOfSections</b>, please reload the app after that. These
                changes work properly only after reloading.
              </li>
              <br />
              <li>
                Have a look at{' '}
                <a
                  style={{color: theme==='dark'? 'skyblue' : 'blue'}}
                  target='_blank'
                  href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/511'
                  rel='noreferrer'
                >
                  issue #511
                </a>
              </li>
              <br />
              <li>
                Consider going through the{' '}
                <a
                  style={{color: theme==='dark'? 'skyblue' : 'blue'}}
                  target='_blank'
                  href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/BarChart/BarChartProps.md'
                  rel='noreferrer'
                >
                  props table
                </a>
              </li>
            </ol>
            <p>
              <span className={styles.noteText}>Update:</span> Starting from
              version <b>1.4.2</b>, the relation-{' '}
              <i>
                <span style={{
                  paddingLeft: 8,
                  color: 'skyblue'
                }}>
                  maxValue
                </span>
                {' = '}
                <span
                style={{
                  color: 'orange'
                }}
              >
                  noOfSections
                </span>
                {' * '}
                <span
                style={{
                  color: 'orange'
                }}
              >
                  stepValue
                </span>
              </i>{' '}
              is now handled internally by the library. The above issue should
              no loger exist.
            </p>
          </p>
        </div>

        <hr />

        <h4 id={headers.dataArray.id}>Understanding the data arary</h4>
        <div style={{ marginLeft: 10 }}>
          <b>data</b> is an array of objects, each object representing a bar in
          the bar chart.
          <br />
          Each object can have the following keys, defining the properties of
          the bar it represents-
          <ol>
            <li className={styles.listItem}>
              <span className={styles.prop}>value</span>
              <div>Value of the item representing height of the bar</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>barWidth</span>
              <div>Width of the bar</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>onPress</span>
              <div>Function called on pressing the bar</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>disablePress</span>
              <div>To disable the press action, defaults to false</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>frontColor</span>
              <div>Color of the bar</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>label</span>
              <div>Label text appearing below the bar (under the X axis)</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>labelTextStyle</span>
              <div>Style object for the label text appearing below the bar</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>labelComponent</span>
              <div>
                Custom label component appearing below the bar. A normal text as
                label may not fit your needs, you can give your own label
                component.
              </div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>topLabelComponent</span>
              <div>Custom component appearing above the bar</div>
            </li>
            <li className={styles.listItem}>
              <span className={styles.prop}>barBorderRadius</span>
            </li>

            <li className={styles.listItem}>
              <span className={styles.prop}>barBorderTopLeftRadius</span>
            </li>

            <li className={styles.listItem}>
              <span className={styles.prop}>barBorderTopRightRadius</span>
            </li>

            <li className={styles.listItem}>
              <span className={styles.prop}>barBorderBottomLeftRadius</span>
            </li>

            <li className={styles.listItem}>
              <span className={styles.prop}>barBorderBottomRightRadius</span>
            </li>

            <li className={styles.listItem}>
              <span className={styles.prop}>spacing</span>
              <div>Space towards the right of the Bar</div>
            </li>
          </ol>
        </div>
        <ProTip
          body={() => {
            return (
              <>
                All of the above properties of the data item, except{' '}
                <b>value</b>, can be used directly as a prop. When used directly
                as a prop, it applies to all the bars. When used as a property
                in the data object, it applies to that particular data item.
              </>
            )
          }}
        />

        <hr />

        <h4 id={headers.showGradient.id}>Gradient</h4>
        <div style={{ marginLeft: 10 }}>
          Gradients can make your chart outstanding. You just need to add the{' '}
          <b>showGradient</b> prop.
          <br />
          Currently we are supporting Linear gradient only.
          <p>
            Gradient colors can be supplied using <b>gradientColor</b> and{' '}
            <b>frontColor</b> props.
            <br />
            The gradient is applied from bottom to top of a bar. The{' '}
            <i>gradientColor</i> takes the bottom position and <i>frontColor</i>{' '}
            takes the top position in the bar.
            <br />
            The default value for <i>gradientColor</i> is white and the default
            value for <i>frontColor</i> is black.
          </p>
          <p>Now let's try making the below chart-</p>
          <img src={data[4].imgSrc} className={styles.barOutput} alt='' />
          <p>
            <span className={styles.noteText}>Note:</span> By default, the y
            axis labels will be rounded to whole number. To show fractional
            numbers on the y axis, use the <b>showFractionalValues</b> prop.
          </p>
          <p>So, the final code for the above chart will be-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[4].code}
          </SyntaxHighlighter>
          <p>
            Did you notice the <b>showYAxisIndices</b> and the{' '}
            <b>showXAxisIndices</b> props?
            <br />
            Using these props adds dots (or small lines) on the axes at each
            label, thus making it graduated like this-
          </p>
          <img
            src={require('../../../assets/indices.png')}
            className={styles.barOutput}
            alt=''
          />
          <p>Othe props related to the axes indices are-</p>
          <ul>
            <li>yAxisIndicesHeight</li>
            <li>yAxisIndicesWidth</li>
            <li>yAxisIndicesColor</li>
            <li>xAxisIndicesHeight</li>
            <li>xAxisIndicesWidth</li>
            <li>xAxisIndicesColor</li>
          </ul>
        </div>

        <hr />
        <h4 id={headers.prefixSuffix.id}>Prefix and Suffix</h4>
        <div style={{ marginLeft: 10 }}>
          Sometimes, the y-axis has values like- $10, $20, $30... or $10k, $20k,
          $30k... <br />
          The data for the above cases would have been [10, 20, 30...].
          <br />
          <br />
          We can use the prop <b>yAxisLabelPrefix</b> to add the prefix-{' '}
          <b>`$`</b> (or any other prefix) before each label on the y-axis.
          <br />
          Similary, we can use the <b>yAxisLabelSuffix</b> prop to add a suffix
          after each label on the y-axis.
        </div>
        <br />

        <hr />
        <h4 id={headers.yAxis.id}>yAxisLabelTexts and formatYLabel</h4>
        <div style={{ marginLeft: 10 }}>
          Adding prefix or suffix may not always be enough. We might need more
          customisations for the Y-axis lables.
          <br />
          We can pass our own array of strings that will be rendered as Y axis
          labels using the <b>yAxisLabelTexts</b> prop. We need to make sure
          that the length of the <b>yAxisLabelTexts</b> array is same as the
          number of sections <b>noOfSections</b> in the Y-axis
          <br />
          <br />
          Sometimes we don't have a predefined array of strings to render as
          labels along the Y-axis. We want the library to generate the Y-axis
          labels but also want to modify/customise it.
          <br />
          For example- we have values in multiples of 1000s but we want the
          lables to be displayed as 1k, 2k, 3k etc.
          <br />
          In such a case, we can use the <b>formatYLabel</b> prop.{' '}
          <b>formatYLabel</b> is a callback function that takes the label
          generated by the library and modifies it. The modified label returned
          by the callback function will be finally rendered along the Y-axis.
          <br />
          <br />
          The prototype of the formatYLabel callback function is-
          <br />
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {`(label: string) => string;`}
          </SyntaxHighlighter>
        </div>
        <br />

        <hr />
        <h4 id={headers.xAxisLabels.id}>Customizing the X-axis labels</h4>
        <div style={{ marginLeft: 10 }}>
          X-axis labels appear just under the X axis. We can customize the X
          axis labels using the <b>xAxisLabelTextStyle</b> prop.
          <br />
          Use the <b>rotateLabel</b> prop to rotate the X axis labels.
          <br />
          Use the <b>xAxisTextNumberOfLines</b> prop to render X axis labels in
          more than 1 line.
          <p>
            The height of X axis labels container is computed automatically.
            Sometimes you may need to explicitly provide the height of X-axis
            labels container. An example case is when the font size of label
            texts is large. Use the <b>xAxisLabelsHeight</b> prop if the labels
            appear cropped from bottom.
          </p>
          <span className={styles.noteText}>Note: </span> Using the{' '}
          <b>xAxisLabelsHeight</b> prop may shift the X axis labels up or down,
          making the label text overlap with the chart content. In such a case
          you can use the <b>xAxisLabelsVerticalShift</b> prop to adjust the
          vertical position of X axis labels.
          <p>
            Have a look at{' '}
            <a
              style={{color: theme==='dark'? 'skyblue' : 'blue'}}
              target='_blank'
              href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/433'
              rel='noreferrer'
            >
              this issue
            </a>
          </p>
        </div>

        <h4 id={headers.xAxisShift.id}>{headers.xAxisShift.label}</h4>
        <div style={{ marginLeft: 10 }}>
          Both <b>xAxisLabelsVerticalShift</b> and{' '}
          <b>labelsDistanceFromXaxis</b> are used to shift the X-axis labels
          upwards or downwards.
          <br />
          In fact, in most cases both will do the same thing. The difference
          arises when we have a combination of +ve and -ve values and{' '}
          <b>autoShiftLabels</b> is set to true.
          <br />
          <br />
          If we both +ve and -ve values and <b>autoShiftLabels</b> is true,
          then-
          <ul>
            <li>
              <b>xAxisLabelsVerticalShift</b> moves all the labels upwards or
              downwards
            </li>

            <li>
              <b>labelsDistanceFromXaxis</b> moves the labels of positive bars
              downwards and moves the labels of negative bars upwards, as shown
              below-
            </li>
            <img
              src={require('../../../assets/xAxisShift.png')}
              style={{ width: 1000, marginTop: 20 }}
              alt=''
            />
          </ul>
        </div>

        <hr />
        <h4 id={headers.touchScroll.id}>
          Touchable, onPress and disableScroll
        </h4>
        <div style={{ marginLeft: 10 }}>
          The bars are by default touchable, that means in each of the above
          charts, the user can press a bar. There are 2 ways to handle the press
          event-
          <ol>
            <li>
              <b>onPress prop</b>: You can directly pass onPress as a prop and
              it will accept 2 parameters- <i>item</i> and <i>index</i>
              <br />
              Here's an example-
              <SyntaxHighlighter
                showLineNumbers
                lineNumberStyle={lineStyle}
                language='javascript'
              >
                {`<BarChart
data = {data}
onPress = {(item,index)=>console.log('item',item)}/>`}
              </SyntaxHighlighter>
            </li>
            <li>
              <b>onPress property in data object</b>: If you pass a function to
              the <b>onPress</b> key in a particular bar object in the data
              array, it will be called when the user presses that bar.
            </li>
          </ol>
          <p>
            To disable touch events, pass the <b>disablePress</b> prop.
          </p>
          <p>
            Similarly, the charts are by default scrollable, that means in each
            of the above charts, the user can scroll it horizontally.
            <br />
            To disable scroll, pass the <b>disableScroll</b> prop.
          </p>
          <p>
            By default, the scroll indicator is set to invisible. To show the
            scroll indicator on scroll, pass the <b>showScrollIndicator</b>{' '}
            prop.
          </p>
        </div>

        <hr />

        <h4 id={headers.scrollRef.id}>
          scrollRef, scrollToEnd and scrollToIndex
        </h4>
        <div style={{ marginLeft: 10 }}>
          <ol>
            <li>
              <b>scrollRef</b> You can create a ref using React.useRef() and
              pass it into the scrollRef prop. Now the ref can be used to scroll
              to a given position.
            </li>
            <li>
              <b>scrollToEnd</b> scrolls to the last bar
            </li>
            <li>
              <b>scrollToIndex</b> scrolls to the given index bar
            </li>
          </ol>
        </div>

        <hr />

        <h4 id={headers.threeD.id}>Three dimensional Bars</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            To render 3D bar chart just pass the prop <b>isThreeD</b>
            <br />
            The simplest 3D Bar chart can be made with just-
            <SyntaxHighlighter
              showLineNumbers
              lineNumberStyle={lineStyle}
              language='javascript'
            >
              {data[5].code}
            </SyntaxHighlighter>
          </p>
          <h5>Output-</h5>
          <img src={data[5].imgSrc} className={styles.barOutput} alt='' />

          <p>
            In 3D bars, we can divide a bar into 3 parts-
            <ol>
              <li>front</li>
              <li>side</li>
              <li>top</li>
            </ol>
            So, we have the following props-
            <ul>
              <li>frontColor</li>
              <li>sideColor</li>
              <li>topColor</li>
              <li>
                <b>sideWidth</b>
                <i> (defaults to half of the bar width)</i>
              </li>
              <li>
                <b>side</b>{' '}
                <i>
                  ('left' or 'right', defaults to 'left' as in the above chart)
                </i>
              </li>
            </ul>
            All the above props can be passed directly into the BarChart, as
            well as through the data array.
            <br />
            <span className={styles.noteText}>Note: </span> whenever we have the
            same prop passed both directly and through data array, the data
            array value will be considered
          </p>

          <p>Now let's try making this beautiful 3D Bar chart-</p>
          <img src={data[6].imgSrc} className={styles.barOutput} alt='' />

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[6].code}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h4 id={headers.animated.id}>Animated charts</h4>
        <div style={{ marginLeft: 10 }}>
          <i>Aha!</i> now comes the most striiking feature - Animation. No
          doubt, it <i>brings life to your data.</i>
          <br />
          Animation not only appears when the chart is mounted but also whenever
          there is any change in the data!
          <p>
            Just pass the prop <b>isAnimated</b> to any of you charts and that's
            it. You chart appears with animation.
          </p>
          <p>Let's try making a simple animated chart like -</p>
          <video
            ref={videoRef}
            muted
            autoPlay
            onEnded={onVideoEnd}
            src={require('../../../assets/animBar.mov')}
          />
          <p style={{ fontSize: 24 }}>
            {isPlaying ? (
              <FaPause onClick={onplayClick} />
            ) : (
              <FaPlay onClick={onplayClick} />
            )}
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[7].code}
          </SyntaxHighlighter>
          <p>
            The animations provided with this package are smooth and performant.
            Almost all the animations are implemented using LayoutAnimation
            provided by react-native.
          </p>
        </div>

        <hr />

        <h4 id={headers.secondaryYAxis.id}>Y axis on both sides</h4>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          We can render the Y axis on the right side of the chart using{' '}
          <b>{yAxisSidePropText}</b>
          <br />
          But, if we need Y axis on both left and right sides, we can use the{' '}
          <b>secondaryYAxis</b> prop.
          <br />
          <br />
          <b>secondaryYAxis</b> is an object of type of{' '}
          <b>secondaryYAxisType</b>. You can control the properties of secodary
          Y axis by passing an object of properties. Following is the type
          definition of the secondaryYAxisType object
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='typescript'
          >
            {secondaryYAxisType}
          </SyntaxHighlighter>
          <span className={styles.noteText}>Note: </span> secondaryYAxis can
          also be passed a boolean value. When passed true, it just renders a
          secondary Y axis on the right side identical to the one on the left.
        </div>

        <hr />

        <h4 id={headers.cap.id}>Capped Bars</h4>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          Yet another trending bar chart design pattern is- putting caps at the
          top of the bars.
          <br />
          So we have a prop called <b>cappedBars</b>
          <p>The props related to capped Bar chart are-</p>
          <ul>
            <li>capThickness</li>
            <li>capColor</li>
            <li>capRadius</li>
          </ul>
          <p>Let's try making the below capped Bar chart-</p>
          <img src={data[8].imgSrc} className={styles.barOutput} alt='' />
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[8].code}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h4 id={headers.customised.id}>Customised Chart</h4>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>Sometimes we need to compare two items as shown below -</p>

          <img src={data[9].imgSrc} className={styles.bigBarOutput} alt='' />

          <p>
            Notice that this chart can be achieved by adjusting the spacing
            between 2 consecutive bars, and providing blue and red color to
            alternate bars.
          </p>
          <p>
            Also, we provide the label text to alternating bars. And we manually
            adjust the label width.
          </p>
          <p>The props used for this chart are-</p>
          <ul>
            <li>spacing</li>
            <li>labelWidth</li>
            <li>labelTextStyle</li>
            <li>roundedTop</li>
            <li>roundedBottom</li>
          </ul>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[9].code}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h4 id={headers.pattern.id}>Background pattern (tile) for bars</h4>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>Here's a chart with a background pattrern background on bars -</p>
          <img src={data[10].imgSrc} className={styles.barOutput} alt='' />
          <p>
            This can be achieved using the props <b>barBackgroundPattern</b> and{' '}
            <b>patternId</b>.<br />
            The barBackgroundPattern should be a Svg Pattern element.
          </p>
          <p>Here's the pattern element used for above chart-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {`const MyPattern = () => {
return (
  <Pattern
    id="DiagonalLines"
    patternUnits="userSpaceOnUse"
    x="0"
    y="0"
    width={60}
    height={14}
    viewBox="0 0 40 20">
    <Path
      d="M 1220 38 L 465 0"
      stroke={'white'}
      strokeWidth={1.5}
      transform={{rotation: '-16'}}
    />
  </Pattern>
);
};`}
          </SyntaxHighlighter>
          <span className={styles.noteText}>Note: </span> The <i>id</i> of the{' '}
          <b>Pattern</b> element must be passed as <b>patternId</b> as a prop
          for the Bar chart.
          <p>
            Notice the <i>Path</i> element in the <b>MyPattern</b> component.
            The prop <b>d</b> is handcrafted to render a diagonal line, and is
            rotated by a precise angle with the <b>transform</b> prop.
            <br />
            <br />
            The complete code for the above chart is-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[10].code}
          </SyntaxHighlighter>
          <h4>Pattern in Specific Bars</h4>
          <p>
            Patterns can be set in certain specific bars by passing the{' '}
            <b>barBackgroundPattern</b> and the <b>patternId</b> as object
            properties in the data array.
            <br />
            Here's an example-
          </p>
          <img src={data[11].imgSrc} className={styles.barOutput} alt='' />
          <p>The data array for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[11].code}
          </SyntaxHighlighter>
          <p>
            Note that we can pass any Svg pattern (not just lines). Let's say we
            want this pattern -
          </p>
          <img src={data[12].imgSrc} className={styles.barOutput} alt='' />
          <p>The pattern component for the above chart will be-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[12].code}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h3 id={headers.negative.id}>Negative values</h3>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>Some charts have negative data values too. For example-</p>
          <img
            src={require('../../../assets/barNegative.png')}
            className={styles.bigBarOutput}
            alt=''
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {negativeBar}
          </SyntaxHighlighter>
          <p>
            Everything seems fine except the x axis labels (<i>Wed</i> and{' '}
            <i>Fri</i>) are getting hidden behind the bars for negative values.
            <br />
            One way to overcome this issue is to display the labels above the x
            axis for the negative values. As it is shown in the chart below-
          </p>
          <img
            src={require('../../../assets/barNegativeAutoShiftLabels.png')}
            className={styles.bigBarOutput}
            alt=''
          />
          <p>
            To achieve this we just have to pass the prop <b>autoShiftLabels</b>
            . So the new code will be-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {`return <BarChart data={barData} autoShiftLabels />`}
          </SyntaxHighlighter>
          <p>While using negative values, you may need to pass these props-</p>
          <ol>
            <li>maxValue</li>
            <li>mostNegativeValue</li>
            <li>stepValue</li>
            <li>stepHeight</li>
            <li>height</li>
            <li>noOfSections</li>
            <li>noOfSectionsBelowXAxis</li>
          </ol>
          <p>
            <b>mostNegativeValue</b> here represents the most negative value to
            be seen on the Y-Axis.
            <br />
            <b>noOfSections</b> means the number of horizontal sections (strips)
            above the X-Axis in the chart.
            <br />
            <b>noOfSectionsBelowXAxis</b> means the number of horizontal
            sections (strips) below the X-Axis in the chart.
            <br />
            <b>height</b> represents the height of the chart above the X-Axis (
            <b>
              <i>excluding</i>
            </b>{' '}
            the part below the X-Axis).
            <br />
          </p>
          <h3>All negative values</h3>
          <p>
            In case there are only negative values in your data set, and no
            positive values, all the bars will appear below the X axis - in the
            fourth quadrant.
            <br />
            But you may want them to appear above the X axis - in the first
            quadrant (since all of them are negative).
            <br />
            For this, you can set the data values as positive (their absolute
            values), and add the prefix "<b>-</b>" with the help of the{' '}
            <b>yAxisLabelPrefix</b> prop.
          </p>
        </div>
        <hr />

        <h3 id={headers.yAxisSide.id}>y axis on right side</h3>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>
            We can add the prop <b>{yAxisSidePropText}</b> to render the y axis
            labels on the right side.
            <br />
            While setting yAxisSide to{' '}
            <b>
              <i>'right'</i>
            </b>
            , make sure to specify the width of the chart, using the{' '}
            <b>width</b> prop.
            <br />
            The default value of the yAxisSide prop is{' '}
            <b>
              <i>yAxisSides.LEFT</i>
            </b>
            .
          </p>
        </div>

        <hr />

        <h3 id={headers.referenceLine.id}>Reference line (horizontal)</h3>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>
            Some charts have one or more horizontal line(s) across the chart.
            They can be called as "
            <b>
              <i>reference lines</i>
            </b>
            ".
            <br />
            Here's an example with one reference line-
          </p>
          <img
            src={
              'https://user-images.githubusercontent.com/31923811/142878579-5120f825-3d31-41cb-be4c-770d624f5159.png'
            }
            className={styles.bigBarOutput}
            style={{ width: 260 }}
            alt=''
          />
          <p>
            To render reference lines we can use the props-{' '}
            <b>showReferenceLine1, showReferenceLine2</b> and{' '}
            <b>showReferenceLine3</b>.<br />
            Currently only 3 reference lines are supported.
          </p>
          <p>
            The position of the reference line can be set using the{' '}
            <b>referenceLine1Position</b> or in general,{' '}
            <b>
              referenceLine<i>{`{n}`}</i>Position
            </b>{' '}
            prop.
            <br />
            Other properties like{' '}
            <b>
              <i>type, color, thickness, dashWidth, dashGap</i>
            </b>{' '}
            can be provided in the <b>referenceLine1Config</b> prop or in
            general,{' '}
            <b>
              referenceLine<i>{`{n}`}</i>Config
            </b>{' '}
            prop.
          </p>
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {chartWithReferenceLine1}
          </SyntaxHighlighter>
        </div>
        <hr />

        <h3 id={headers.barMarginBottom.id}>
          barMarginBottom - bars lifted above X axis
        </h3>
        <div>
          <p>
            Some charts have a margin between the bottom of the bars and the x
            axis. For example-
          </p>
          <img
            src={require('../../../assets/barMargin.png')}
            className={styles.barOutput}
            alt=''
          />
          <p>
            To render a chart like this, we just need to pass the{' '}
            <b>barMarginBottom</b> prop.
            <br />
            The code for the above chart is-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {barMargin}
          </SyntaxHighlighter>
          <p>
            We can also have different values of marginBottom for different bars
            in the same chart.
            <br />
            In such a case, we can add the <b>barMarginBottom</b> property
            separately to the data objects.
            <br />
            Here's an example-
          </p>
          <img
            src={require('../../../assets/multiBarMargin.png')}
            className={styles.barOutput}
            alt=''
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {multiBarMargin}
          </SyntaxHighlighter>
        </div>
        <hr />

        <h3 id={headers.barStyle.id}>Custom Bar styles</h3>
        <div>
          <p>
            Most of the properties of the Bars are controlled directly via props
            like <i>frontColor</i>, <i>barWidth</i>, <i>showGradient</i>,{' '}
            <i>gradientColor</i> etc. However, styles like <b>border</b>,{' '}
            <b>shadow</b>, <b>elevation</b> and many others can be provided via
            the <b>barStyle</b> object.
          </p>
          <p>Here's an example-</p>
          <img
            src={
              'https://github.com/Abhinandan-Kushwaha/chart-images/blob/main/screenshots/neon3DStill.png?raw=true'
            }
            className={styles.bigBarOutput}
            alt=''
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {neonBars3D}
          </SyntaxHighlighter>
        </div>

        <hr />

        <h3 id={headers.tooltip.id}>Tooltip</h3>
        <div>
          <p>
            Some charts show a tooltip when user taps on a Bar. Tolltip provides
            extra information about the bar.
            <br />
            To add tooltip to your bar chart, just add the <b>
              renderTooltip
            </b>{' '}
            prop to your BarChart component.
            <br />
            It is a function that returns the custom tooltip component. It takes{' '}
            <i>item</i> and <i>index</i> as parameters.
          </p>
          <p>
            For an example, let's try to add the <b>renderTooltip</b> prop to
            the above chart.
            <br />
            So the new code will be-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {neonBars3DWithTooltip}
          </SyntaxHighlighter>
          <p>
            Notice that we have only added the <b>renderTooltip</b> prop, and
            just for beautification, I also added the <b>isAnimated</b> prop.
          </p>
          <p>The output of the code is-</p>
          <img
            src={
              'https://github.com/Abhinandan-Kushwaha/chart-images/blob/main/recordings/bar3dTooltipNeon.gif?raw=true'
            }
            className={styles.bigBarOutput}
            alt=''
          />
          <p>
            The most common issue with tooltip is that it gets truncated or
            overflown for the rightmost bars. To solve this, we can use the prop{' '}
            <b>leftShiftForLastIndexTooltip</b>. It accepts a number and shifts
            the tooltip of last bar by the given number.
          </p>
          <p>
            Apart from the <b>leftShiftForLastIndexTooltip</b>, we now have the{' '}
            <b>leftShiftForTooltip</b> prop which we can add to selected Bars.
            So, suppose you have 12 bars and the 6th bar reaches to the end of
            screen, you can add <b>leftShiftForTooltip</b> to 6th, 7th, 8th,
            9th, 10th, 11th and 12th bars.
          </p>
          <p>
            This{' '}
            <b>
              <a
                style={{color: theme==='dark'? 'skyblue' : 'blue'}}
                target='_blank'
                href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/240'
              >
                Git issue
              </a>
            </b>{' '}
            might be helpful.
          </p>
        </div>

        <hr />

        <h3 id={headers.combinedBarLine.id}>Combined Bar and Line chart</h3>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>
            You may want to render a Line along with the Bar chart.
            <br />
            That's really simple. Just add the prop <b>showLine</b> and that's
            it!
          </p>
          <p>Here's a quick example-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {barLineSimple}
          </SyntaxHighlighter>
          <p>The output of the above code is-</p>
          <img
            src={
              'https://github.com/Abhinandan-Kushwaha/chart-images/blob/main/screenshots/barLineSimple.png?raw=true'
            }
            className={styles.bigBarOutput}
            alt=''
          />
          <p>
            See the line appearing along the bars. The line can be styled using
            the <b>lineConfig</b> prop. The <b>lineConfig</b> prop is an object
            that allows most of the props allowed by the <b>{`<LineChart>`}</b>{' '}
            component.
            <br />
            Jump to{' '}
            <b>
              <a style={{color: theme==='dark'? 'skyblue' : 'blue'}} target='_blank' href={`${domain}linechart`}>
                this page
              </a>
            </b>{' '}
            to know in detail about Line charts.
            <br />
            <br />
          </p>
          <p>
            The properties allowed by the <b>lineConfig</b> prop are-
            <br />
            <br />
            <table>
              <tr>
                <th>prop</th>
                <th>type</th>
                <th>default</th>
              </tr>
              <tr>
                <td>initialSpacing</td>
                <td> number</td>
                <td>initialSpacing</td>
              </tr>
              <tr>
                <td>curved</td>
                <td> Boolean</td>
                <td>false</td>
              </tr>
              <tr>
                <td>isAnimated</td>
                <td> Boolean</td>
                <td>false</td>
              </tr>
              <tr>
                <td>delay</td>
                <td> number</td>
                <td>0</td>
              </tr>
              <tr>
                <td>thickness</td>
                <td> number</td>
                <td>1</td>
              </tr>
              <tr>
                <td>color</td>
                <td> ColorValue | String | any</td>
                <td>'black'</td>
              </tr>
              <tr>
                <td>hideDataPoints</td>
                <td> Boolean</td>
                <td>false</td>
              </tr>
              <tr>
                <td>dataPointsShape</td>
                <td> String</td>
                <td>'circular'</td>
              </tr>
              <tr>
                <td>dataPointsWidth</td>
                <td> number</td>
                <td>2</td>
              </tr>
              <tr>
                <td>dataPointsHeight</td>
                <td> number</td>
                <td>3</td>
              </tr>
              <tr>
                <td>dataPointsColor</td>
                <td> ColorValue | String | any</td>
                <td>'black'</td>
              </tr>
              <tr>
                <td>dataPointsRadius</td>
                <td> number</td>
                <td>3</td>
              </tr>
              <tr>
                <td>textColor</td>
                <td> ColorValue | String | any</td>
                <td>'gray'</td>
              </tr>
              <tr>
                <td>textFontSize</td>
                <td> number</td>
                <td>10</td>
              </tr>
              <tr>
                <td>textShiftX</td>
                <td> number</td>
                <td>0</td>
              </tr>
              <tr>
                <td>textShiftY</td>
                <td> number</td>
                <td>0</td>
              </tr>
              <tr>
                <td>shiftY</td>
                <td> number</td>
                <td>0</td>
              </tr>
              <tr>
                <td>startIndex</td>
                <td> number</td>
                <td>0</td>
              </tr>
              <tr>
                <td>endIndex</td>
                <td> number</td>
                <td>lineData.length - 1</td>
              </tr>
              <tr>
                <td>showArrow</td>
                <td> boolean</td>
                <td>false</td>
              </tr>
              <tr>
                <td>arrowConfig</td>
                <td> arrowType</td>
                <td>defaultArrowConfig</td>
              </tr>
            </table>
          </p>
          <p>
            By default the Line will be rendered afront the Bars in case they
            overlap. However, it can be forced to appear behind the Bars by
            setting the <b>lineBehindBars</b> prop to true. It's default value
            is false.
            <br />
            You can also visit the{' '}
            <b>
              <a
                style={{color: theme==='dark'? 'skyblue' : 'blue'}}
                target='_blank'
                href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/BarChart/BarChartProps.md'
              >
                Bar Chart props
              </a>
            </b>{' '}
            page to know more about the properties allowed by the{' '}
            <b>lineConfig</b> prop and their default values.
          </p>
          <p>
            Here's a beautiful chart made by combining a Line chart with a Bar
            chart. This is done by using the <b>showLine</b> and{' '}
            <b>lineConfig</b> props of the BarChart component.
          </p>
          <img
            src={
              'https://github.com/Abhinandan-Kushwaha/chart-images/blob/main/screenshots/barLine.png?raw=true'
            }
            style={{ width: 380, borderRadius: 20 }}
            alt=''
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {beautifulCombinedBarLine}
          </SyntaxHighlighter>
        </div>
        <hr />
      </div>
    </div>
  )
}
